import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Alert, Heading, Link } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { CONFIG } from "config";
import { useDocumentSigningUrl, useDocumentsToSign } from "data/ss-data-hooks";
import { setCookie, trackEvent, useHideSupportChat } from "utils";

import styles from "./embedded-docusign.module.scss";

export const EmbeddedDocusign = () => {
  useHideSupportChat();
  const navigate = useNavigate();
  const [error, setError] = useState<boolean>(false);
  const { envelopeId } = useParams();
  const {
    data: signingUrlData,
    isLoading: signingUrlLoading,
    error: signingUrlError,
  } = useDocumentSigningUrl(envelopeId ?? "");
  const { data: documentsToSignData, isLoading: documentsToSignLoading } =
    useDocumentsToSign();
  const documentToSign = documentsToSignData?.find(
    (doc) => doc.id === envelopeId
  );
  const documentName = documentToSign?.documentType ?? "Document signature";
  const signingUrl = signingUrlData?.signingLink ?? "";
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const signingRef = useRef<{
    on: (event: string, callback: (event: unknown) => void) => void;
    mount: (container: string) => void;
    destroy: () => void;
  } | null>(null);
  const docusignContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setError(!!signingUrlError);
  }, [signingUrlError]);

  useEffect(() => {
    // Only initialize if the container and signing URL are available and the signingRef is not already initialized.
    if (!docusignContainerRef.current || !signingUrl || signingRef.current) {
      return;
    }

    // Remove any existing script if present
    if (scriptRef.current && scriptRef.current.parentNode) {
      scriptRef.current.parentNode.removeChild(scriptRef.current);
      scriptRef.current = null;
    }

    const scriptTag = document.createElement("script");
    const scriptSrc =
      CONFIG.APP_ENV === "production"
        ? "https://js.docusign.com/bundle.js"
        : "https://js-d.docusign.com/bundle.js";

    scriptTag.src = scriptSrc;
    scriptTag.async = true;
    scriptTag.onload = async () => {
      if (!signingUrl) {
        setError(true);
        trackEvent("Embedded docusign error", {
          error: "Signing URL not available",
        });
        return;
      }

      const clientId = "";
      const signingConfiguration = {
        url: signingUrl,
        displayFormat: "focused",
        style: {
          branding: {
            primaryButton: {
              // Color match for Alto DS primary button:
              backgroundColor: "#267DAF",
              color: "white",
            },
          },
          downloadModal: {
            show: true,
          },
        },
      };
      try {
        if (window.DocuSign) {
          const docusignClient = await window.DocuSign.loadDocuSign(clientId);
          signingRef.current = docusignClient.signing(signingConfiguration);
          signingRef.current.on("ready", () => {
            trackEvent("Embedded docusign ready", { documentName });
          });
          signingRef.current.on(
            "sessionEnd",
            (event: { sessionEndType: string }) => {
              trackEvent("Embedded docusign session end", {
                sessionEndType: event?.sessionEndType,
              });
              navigate("/my/documents");
              if (event?.sessionEndType === "signing_complete") {
                setCookie("hide-documents-to-sign", true, 5 * 60);
              }
            }
          );

          signingRef.current.mount("#docusign-container");
        } else {
          throw new Error("DocuSign library not available");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        trackEvent("Embedded docusign error", { error: errorMessage });
        setError(true);
      }
    };

    scriptTag.onerror = () => {
      trackEvent("Embedded docusign error", {
        error: "Failed to load DocuSign script",
      });
      setError(true);
      scriptRef.current = null;
    };

    scriptRef.current = scriptTag;
    document.body.appendChild(scriptTag);
  }, [signingUrl, documentName, navigate]);

  useEffect(() => {
    // Cleanup effect to remove script on unmount
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
        scriptRef.current = null;
      }
      if (signingRef.current) {
        try {
          signingRef.current.destroy();
        } catch (_err) {
          // Ignore errors during cleanup
        }
        signingRef.current = null;
      }
    };
  }, []);

  if (signingUrlLoading || documentsToSignLoading) {
    return <BaseLayout loading hideFooter hideHeader />;
  }

  return (
    <BaseLayout hideFooter hideHeader>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <Heading size="md">{documentName}</Heading>
          <Link
            appearance="primary"
            data-testid="embedded-docusign-close-btn"
            href="/my/documents"
            onPress={() => trackEvent("Embedded Docusign Close button clicked")}
            size="sm"
          >
            Close
          </Link>
        </div>
      </header>

      {error && (
        <div className={styles.errorAlert}>
          <Alert
            title="There was an error loading the document to sign."
            appearance="danger"
            description="Try again in a few minutes or contact customer support if the problem persists."
          />
        </div>
      )}
      <div
        id="docusign-container"
        className={styles.docusignContainer}
        ref={docusignContainerRef}
      />
    </BaseLayout>
  );
};
